import React from 'react';
import { TrendingUp, Users, AlertCircle, DollarSign, Calendar, Target } from 'lucide-react';
import type { AnalysisExecutiveSummary } from '../../../types/acquisitionAnalysis';

interface ExecutiveSummaryProps {
  data: AnalysisExecutiveSummary;
}

export const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({ data }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  // Build metrics array with new last month trail cards
  const metrics: Array<{
    label: string;
    value: string;
    icon: typeof DollarSign;
    color: string;
    sublabel?: string;
  }> = [];

  // Last Month Trail - prominently displayed
  if (data.lastMonthTrail !== undefined && data.lastMonthTrail > 0) {
    metrics.push({
      label: 'Last Month Trail',
      value: formatCurrency(data.lastMonthTrail),
      sublabel: data.lastMonthLabel || '',
      icon: Calendar,
      color: 'purple',
    });
  }

  // Projected Annual Trail (Last Month × 12)
  if (data.projectedAnnualTrail !== undefined && data.projectedAnnualTrail > 0) {
    metrics.push({
      label: 'Projected Annual Trail',
      value: formatCurrency(data.projectedAnnualTrail),
      sublabel: 'Last Month × 12',
      icon: Target,
      color: 'pink',
    });
  }

  // Standard metrics
  metrics.push(
    {
      label: 'Annual Trail Income',
      value: formatCurrency(data.annualTrailIncome),
      icon: DollarSign,
      color: 'indigo',
    },
    {
      label: 'Year-over-Year Growth',
      value: formatPercent(data.yoyGrowth),
      icon: TrendingUp,
      color: 'green',
    },
    {
      label: 'YTD Growth',
      value: formatPercent(data.ytdGrowth),
      icon: TrendingUp,
      color: 'emerald',
    },
    {
      label: 'Broker Count',
      value: data.brokerCount.toString(),
      icon: Users,
      color: 'blue',
    },
    {
      label: 'Broker Attrition',
      value: data.brokerAttrition === 0 ? 'Zero' : data.brokerAttrition.toString(),
      icon: AlertCircle,
      color: data.brokerAttrition === 0 ? 'green' : 'red',
    },
    {
      label: 'Clawback Rate',
      value: `~${data.clawbackRate.toFixed(1)}%`,
      icon: AlertCircle,
      color: data.clawbackRate < 2 ? 'green' : data.clawbackRate < 5 ? 'yellow' : 'red',
    }
  );

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      indigo: 'bg-indigo-500/20 border-indigo-500/30 text-indigo-300',
      green: 'bg-green-500/20 border-green-500/30 text-green-300',
      emerald: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300',
      blue: 'bg-blue-500/20 border-blue-500/30 text-blue-300',
      yellow: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-300',
      red: 'bg-red-500/20 border-red-500/30 text-red-300',
      purple: 'bg-purple-500/20 border-purple-500/30 text-purple-300',
      pink: 'bg-pink-500/20 border-pink-500/30 text-pink-300',
    };
    return colors[color] || colors.indigo;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Key Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div
                key={index}
                className={`p-4 rounded-lg border ${getColorClasses(metric.color)}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-white mb-1">{metric.value}</p>
                <p className="text-sm text-slate-400">{metric.label}</p>
                {metric.sublabel && (
                  <p className="text-xs text-slate-500 mt-0.5">{metric.sublabel}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
        <p className="text-sm text-indigo-300">
          <strong>Key Insight:</strong> This is a{' '}
          {data.yoyGrowth > 0 ? 'growing' : 'declining'} trail book with{' '}
          {data.ytdGrowth > data.yoyGrowth + 2 ? 'accelerating' :
           data.ytdGrowth < data.yoyGrowth - 5 ? 'decelerating' : 'stable'} momentum.
          {data.brokerAttrition === 0 && ' Zero broker attrition is a strong positive signal.'}
          {data.clawbackRate < 2 && ' Very low clawback rate indicates quality loan book.'}
        </p>
      </div>
    </div>
  );
};
